import React, { Component, ErrorInfo, FC, ReactNode } from 'react';
import { Theme } from '@mui/material';
import { ACTIVE_SEASONAL_THEME } from './activeTheme';
import { SEASONAL_THEMES } from './themes';
import type { SeasonalLoaderProps, SeasonalThemeDefinition, SeasonalThemeName } from './SeasonalTheme';

export type { SeasonalLoaderProps, SeasonalThemeDefinition, SeasonalThemeName };
export { ACTIVE_SEASONAL_THEME };

/**
 * Per-user escape hatch: run `localStorage.setItem('m24.seasonal', 'off')` in the browser console
 * to get the original look for yourself only. `localStorage.removeItem('m24.seasonal')` turns it back on.
 */
const OPT_OUT_STORAGE_KEY = 'm24.seasonal';

const isOptedOut = (): boolean => {
  try {
    return window.localStorage.getItem(OPT_OUT_STORAGE_KEY) === 'off';
  } catch (_e) {
    return false;
  }
};

/** Resolves the theme that should be used right now. */
export const getActiveSeasonalTheme = (): SeasonalThemeDefinition => {
  // Keep the existing test-suite identical to the undecorated app. Theme components have their own tests.
  if (import.meta.env.MODE === 'test' || isOptedOut()) {
    return SEASONAL_THEMES.original;
  }
  return SEASONAL_THEMES[ACTIVE_SEASONAL_THEME] ?? SEASONAL_THEMES.original;
};

/** Wraps the MUI theme with the active theme's overrides (if any). */
export const withSeasonalTheme = (theme: Theme): Theme => {
  const seasonal = getActiveSeasonalTheme();
  return seasonal.extendMuiTheme ? seasonal.extendMuiTheme(theme) : theme;
};

interface SeasonalErrorBoundaryProps {
  fallback: ReactNode;
  children?: ReactNode;
}

/**
 * Decorations must never take the app down with them. If a seasonal component throws, render `fallback`
 * (nothing, or the regular component) instead and log the error.
 */
export class SeasonalErrorBoundary extends Component<SeasonalErrorBoundaryProps, { failed: boolean }> {
  constructor(props: SeasonalErrorBoundaryProps) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('Seasonal theme component failed, falling back to the regular UI', error, info);
  }

  render(): ReactNode {
    const { failed } = this.state;
    const { fallback, children } = this.props;
    return failed ? fallback : children;
  }
}

/** Renders the active theme's header decoration, or nothing. */
export const SeasonalHeaderDecoration: FC = () => {
  const { HeaderDecoration } = getActiveSeasonalTheme();
  if (!HeaderDecoration) {
    return null;
  }
  return React.createElement(SeasonalErrorBoundary, { fallback: null }, React.createElement(HeaderDecoration));
};

/**
 * Renders the active theme's loader, or `fallback` (the regular loader) when the theme has none or it fails.
 */
export const SeasonalLoader: FC<SeasonalLoaderProps & { fallback: React.ReactElement }> = ({ fallback, ...props }) => {
  const { Loader } = getActiveSeasonalTheme();
  if (!Loader) {
    return fallback;
  }
  return React.createElement(SeasonalErrorBoundary, { fallback }, React.createElement(Loader, props));
};
